import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  BadgeCheck,
  Building2,
  ExternalLink,
  Mail,
  MapPin,
  Phone,
  UserRound,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const EMPTY_VALUE = "—";

function InfoRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={650} textAlign="right">
        {value || EMPTY_VALUE}
      </Typography>
    </Box>
  );
}

function SectionHeader({ icon, children }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
      {icon}
      <Typography variant="h6" fontWeight={750}>
        {children}
      </Typography>
    </Stack>
  );
}

export default function CustomerPreviewDrawer({
  open,
  onClose,
  customerIdUid,
  customerName,
  customerAvatar,
  preview,
  loading,
  error,
}) {
  const theme = useTheme();
  const navigate = useNavigate();

  const initials = customerName
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  const generalInfo = preview?.generale;
  const ruiInfo = preview?.RUI;
  const contacts = Array.isArray(preview?.contatti) ? preview.contatti : [];

  const handleOpenFullProfile = () => {
    onClose();
    navigate(`/pages/profile/${customerIdUid}`);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          bgcolor: "background.default",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 3,
          py: 2.5,
          bgcolor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
          <Avatar
            src={customerAvatar}
            alt={customerName}
            sx={{
              width: 44,
              height: 44,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
              fontWeight: 800,
            }}
          >
            {initials || "R"}
          </Avatar>
          <Box minWidth={0}>
            <Typography variant="caption" color="text.secondary">
              Profilo richiedente
            </Typography>
            <Typography variant="h6" fontWeight={750} noWrap>
              {generalInfo?.Nominativo || customerName}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} aria-label="Chiudi pannello richiedente">
          <X size={20} />
        </IconButton>
      </Box>

      <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
        {loading ? (
          <Box
            sx={{
              minHeight: 260,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Stack spacing={1.5} alignItems="center">
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Caricamento dati…
              </Typography>
            </Stack>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : preview ? (
          <Stack spacing={3}>
            <Box
              sx={{
                p: 3,
                borderRadius: 2.5,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <SectionHeader icon={<UserRound size={19} />}>
                Informazioni generali
              </SectionHeader>
              <Stack spacing={1.75} sx={{ pl: 3.75 }}>
                <InfoRow label="Nominativo" value={generalInfo?.Nominativo} />
                <Box
                  sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}
                >
                  <MapPin
                    size={17}
                    color={theme.palette.text.secondary}
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {generalInfo?.Indirizzo || EMPTY_VALUE}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={650}
                      sx={{ mt: 0.25 }}
                    >
                      {generalInfo?.Comune || EMPTY_VALUE}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                p: 3,
                borderRadius: 2.5,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <SectionHeader icon={<BadgeCheck size={19} />}>RUI</SectionHeader>
              <Stack spacing={1.5} sx={{ pl: 3.75 }}>
                <InfoRow label="Sezione" value={ruiInfo?.sezione} />
                <InfoRow label="Codice" value={ruiInfo?.codice} />
                <InfoRow label="Data iscrizione" value={ruiInfo?.data} />
              </Stack>
            </Box>

            <Box
              sx={{
                p: 3,
                borderRadius: 2.5,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <SectionHeader icon={<Building2 size={19} />}>
                Contatti
              </SectionHeader>
              {contacts.length > 0 ? (
                <Stack spacing={1.5} sx={{ pl: 3.75 }}>
                  {contacts.map((contact, index) => {
                    const isEmail =
                      contact.Tipologia?.toLowerCase() === "email";
                    const Icon = isEmail ? Mail : Phone;
                    return (
                      <Box
                        key={`${contact.Tipologia}-${contact.Valore}-${index}`}
                        sx={{
                          display: "flex",
                          gap: 1.25,
                          alignItems: "center",
                        }}
                      >
                        <Icon size={17} color={theme.palette.text.secondary} />
                        <Box minWidth={0}>
                          <Typography variant="caption" color="text.secondary">
                            {contact.Tipologia}
                          </Typography>
                          <Typography variant="body2" fontWeight={650}>
                            {contact.Valore}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ pl: 3.75 }}
                >
                  Nessun contatto disponibile.
                </Typography>
              )}
            </Box>
          </Stack>
        ) : null}
      </Box>

      {customerIdUid ? (
        <Box
          sx={{
            p: 3,
            bgcolor: "background.paper",
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            endIcon={<ExternalLink size={17} />}
            onClick={handleOpenFullProfile}
          >
            Visualizza profilo completo
          </Button>
        </Box>
      ) : null}
    </Drawer>
  );
}
